<template>
  <div class="all-members-cards">
    <!-- Removed zone-filter-container and select dropdown -->

    <div class="cards-container overlap">
      <!-- Show no results message when filteredMembers is empty -->
      <div v-if="filteredMembers.length === 0" class="no-results-container">
        <div class="no-results-message">
          <h3>Aucun membre trouvé</h3>
          <p>Aucun membre ne correspond aux filtres sélectionnés.</p>
          <p>Essayez de modifier vos critères de recherche.</p>
        </div>
      </div>

      <!-- Show cards when there are results -->
      <template v-else>
        <!-- Add previous icon -->
        <div class="icon-prev" @click="showPreviousMembers" v-if="hasPreviousMembers">
          <img
            class="ep-arrow-left-bold-2"
            alt="Ep arrow left bold"
            src="/img/ep-arrow-right-bold-2.svg"
          />
        </div>
        
        <div v-for="(member, index) in currentMembers" :key="startIndex + index" 
             class="card" :class="`card-${index + 1}`"
             @click="navigateToDetail(member.Numero)">
          <div class="frame-5">
            <div class="logo-container">
              <template v-if="getLogoUrl(member)">
                <img
                  v-if="!logoImageErrors[member.Numero]"
                  class="image-5"
                  :alt="(member.displayName || 'Member') + ' Logo'"
                  :src="getLogoUrl(member)"
                  @error="setLogoError(member.Numero)"
                />
                <div v-else class="members-card-text-logo-fallback">
                  {{ member.displayName || 'Logo' }}
                </div>
              </template>
              <template v-else-if="member.displayName">
                <div class="members-card-text-logo-fallback">
                  {{ member.displayName }}
                </div>
              </template>
              <template v-else>
                <div class="members-card-text-logo-fallback">Nom non disponible</div>
              </template>
            </div>
          </div>
          <div class="biolandes-madagascar" :style="getMemberNameStyle(member.displayName)">
            <template v-if="member.displayName">
              <span v-for="(line, i) in member.displayName.split('\n')" :key="i">
                {{ line }}
                <br v-if="i < member.displayName.split('\n').length - 1" />
              </span>
            </template>
          </div>
          <p class="p">
            {{ member.produitsPreview }}
          </p>
          <div class="contact-icon-wrapper">
            <img
              v-if="member.phoneValue"
              class="contact-icon clickable-icon"
              alt="Ic round phone"
              src="/img/ic-round-phone-4.svg"
              @click.stop="callPhone(member.phoneValue)"
              :title="`Appeler ${member.phoneValue}`"
            />
            <img
              v-if="member.emailValue"
              class="contact-icon clickable-icon"
              alt="Material symbols"
              src="/img/material-symbols-mail-4.svg"
              @click.stop="sendEmail(member.emailValue)"
              :title="`Envoyer un email à ${member.emailValue}`"
            />
            <img
              v-if="member.siteValue"
              class="contact-icon clickable-icon"
              alt="Mdi internet"
              src="/img/mdi-internet-4.svg"
              @click.stop="openWebsite(member.siteValue)"
              :title="`Visiter ${member.siteValue}`"
            />
          </div>
        </div>
        
        <div class="icon-next" @click="showNextMembers" v-if="hasMoreMembers">
          <img
            class="ep-arrow-right-bold-2"
            alt="Ep arrow right bold"
            src="/img/ep-arrow-right-bold-2.svg"
          />
        </div>
      </template>
    </div>
    
  </div>
  <div class="pagination-container">
    <PaginationDetail 
      v-if="filteredMembers.length > 0"
      :current-page="currentPage" 
      :total-pages="totalPages"
      @page-changed="handlePageChange" 
    />
  </div>
</template>

<script>
import PaginationDetail from './PaginationDetail.vue';
import membresData from '../../../membres.json';
import memberDetailsData from "../../../member24v.json";
import productsData from "../../../products-zones.json";
import productCategoriesData from "../../../product-categories.json";

export default {
  name: "MembersCards",
  components: {
    PaginationDetail
  },
  props: {
    selectedZone: {
      type: String,
      default: '' // Default to empty string, signifying all zones
    },
    selectedProductCategory: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      baseMembers: membresData,
      startIndex: 0,
      cardsPerPage: 5,
      currentPage: 1,
      logoImageErrors: {} // To track logo loading errors
    };
  },
  computed: {
    mergedMembers() {
      // Build an enriched member object list: start from baseMembers and merge detail entry if exists
      return this.baseMembers.map(base => {
        const detail = memberDetailsData.find(d => String(d.Numero) === String(base.Numero));
        const displayName = (detail && detail.Nom && detail.Nom.trim() !== '')
          ? detail.Nom
          : (base['Nom'] || base['Nom de la société'] || 'Nom non disponible');
        const phoneValue = (detail && detail['Téléphone']) || base['Numéro téléphone'] || base['Téléphone'] || '';
        const emailValue = (detail && (detail['Mail'] || detail['Adresse e-mail'])) || base['Adresse e-mail'] || base['Mail'] || '';
        const siteValue = (detail && (detail['Site web'] || detail['Site Web'])) || base['Site Web'] || base['Site web'] || '';
        const produitsBio = (detail && detail['Produits BIO']) || base['Produits BIO'] || base['Produits Phares'] || '';
        const produitsPreview = produitsBio
          ? produitsBio.substring(0, 100) + (produitsBio.length > 100 ? '...' : '')
          : 'Information non disponible';
        
        // Prefer zone from member24v.json (detail) over membres.json (base)
        const zoneValue = (detail && detail['Zone']) || base['Zone'] || base['Zone '] || '';
        
        return {
          ...base,
          displayName,
          phoneValue,
          emailValue,
          siteValue,
          produitsPreview,
          zoneValue, // Add the preferred zone value
          // Keep original fields accessible if needed
        };
      });
    },
    filteredMembers() {
      let members = this.mergedMembers;

      // Filter by zone (using member24v.json zone convention)
      if (this.selectedZone) {
        const normalizedSelectedZone = this.selectedZone.toLowerCase().trim();
        console.log("MembersCards - Filtering by zone:", this.selectedZone, "normalized:", normalizedSelectedZone);
        
        members = members.filter(member => {
          // Use the merged zoneValue which prioritizes member24v.json zones
          const memberZoneField = member.zoneValue;
          if (!memberZoneField || typeof memberZoneField !== 'string') {
            return false;
          }
          
          const normalizedMemberZone = memberZoneField.toLowerCase().trim();
          const isMatch = normalizedMemberZone === normalizedSelectedZone;
          
          if (isMatch) {
            console.log("Zone match found:", member.displayName, "zone:", memberZoneField);
          }
          
          return isMatch;
        });
        
        console.log("MembersCards - After zone filtering:", members.length, "members found");
      }

      // Filter by product category
      if (this.selectedProductCategory) {
        const keywords = productCategoriesData[this.selectedProductCategory].map(k => k.toLowerCase());
        
        const membersWithProduct = productsData
          .filter(p => 
            p['Produits BIO'] && p['Produits BIO'].some(product => 
              keywords.some(keyword => product.toLowerCase().includes(keyword))
            )
          )
          .map(p => String(p.Numero));

        members = members.filter(member => membersWithProduct.includes(String(member.Numero)));
      }

      return members;
    },
    currentMembers() {
      return this.filteredMembers.slice(this.startIndex, this.startIndex + this.cardsPerPage);
    },
    hasMoreMembers() {
      return this.startIndex + this.cardsPerPage < this.filteredMembers.length;
    },
    hasPreviousMembers() {
      return this.startIndex > 0;
    },
    totalPages() {
      return Math.ceil(this.filteredMembers.length / this.cardsPerPage);
    }
  },
  methods: {
  getMemberNameStyle(name) {
      if (!name) return {};
      const effectiveLength = name.replace('\n', ' ').length;
      if (effectiveLength > 40) {
        return { fontSize: '15px', lineHeight: '1.2' };
      }
      if (effectiveLength > 25) {
        return { fontSize: '17px', lineHeight: '1.2' };
      }
      return {};
    },
    getLogoUrl(member) {
      if (member && member.Numero) {
        return `/logo/${member.Numero}.webp`;
      }
      return null;
    },
    setLogoError(numero) {
      if (numero) {
        this.logoImageErrors[numero] = true;
      }
    },
    showNextMembers() {
      if (this.hasMoreMembers) {
        this.startIndex += this.cardsPerPage;
        this.currentPage++;
      }
    },
    showPreviousMembers() {
      if (this.hasPreviousMembers) {
        this.startIndex -= this.cardsPerPage;
        this.currentPage--;
      }
    },
    handlePageChange(page) {
      this.startIndex = (page - 1) * this.cardsPerPage;
      this.currentPage = page;
      this.logoImageErrors = {}; // Reset errors on page change
    },
    navigateToDetail(numero) {
      if (numero) {
        this.$router.push({ name: 'PageDetailsMembre', params: { id: String(numero) } });
      }
    },
    callPhone(phoneNumber) {
      if (phoneNumber) {
        // Clean the phone number and create tel: link
        const cleanPhone = phoneNumber.replace(/\s+/g, '');
        window.location.href = `tel:${cleanPhone}`;
      }
    },
    sendEmail(email) {
      if (email) {
        // Create mailto: link
        window.location.href = `mailto:${email}`;
      }
    },
    openWebsite(website) {
      if (website) {
        // Ensure the URL has a protocol
        let url = website;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        // Open in new tab
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  },
  watch: {
    selectedZone(newValue) {
      console.log("MembersCards - selectedZone changed to:", newValue);
      this.currentPage = 1;
      this.startIndex = 0;
      this.logoImageErrors = {}; // Reset errors on filter change
    },
    selectedProductCategory() {
      this.currentPage = 1;
      this.startIndex = 0;
      this.logoImageErrors = {};
    },
    // Optional: Watch startIndex if page changes can happen other ways
    // startIndex() {
    //   this.logoImageErrors = {}; 
    // }
  }
};
</script>

<style>

.all-members-cards {
  height: max-content;
  /* left: 247px; */
  /* position: absolute; */
  /* top: 428px; */
  /* width: 1300px; */
      z-index: 0;
  margin: 0px 8vw;
  position: relative;
  display: grid;
  overflow-x: visible;
}

/* No results message styling */
.all-members-cards .no-results-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px; /* Match card height */
  width: 100%;
}

.all-members-cards .no-results-message {
  background: rgba(230, 227, 181, 0.2);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 48px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 400px; /* Match card height */
  width: 800px; /* Made wider */
  padding: 48px;
  box-sizing: border-box;
}

.all-members-cards .no-results-message h3 {
  color: #000000;
  font-family: "Mona-Sans-Bold", Helvetica;
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 16px 0;
  letter-spacing: 0;
  line-height: 1.2;
}

.all-members-cards .no-results-message p {
  color: #000000;
  font-family: "Mona-Sans-Light", Helvetica;
  font-size: 16px;
  font-weight: 300;
  margin: 8px 0;
  letter-spacing: 0;
  line-height: 1.4;
}

/* ... existing styles ... */

.all-members-cards .icon-prev {
  align-items: center;
  background-color: #ffffff1a;
  border-radius: 100px;
  box-shadow: 0px 4px 4px #00000040;
  display: flex;
  gap: 10px;
  height: 36px;
  justify-content: center;
  margin-right: -2px;
  padding: 10px;
  width: 48px;
  z-index: 6;
}

.all-members-cards .ep-arrow-left-bold-2 {
  height: 24px;
  margin-bottom: -4.00px;
  margin-top: -4.00px;
  position: relative;
  width: 24px;
  transform: rotate(180deg);
}

/* ... rest of existing styles ... */


.all-members-cards .cards-container {
  display: flex;
  align-items: center;
  /* flex-direction: row-reverse; */
  gap: -24px;
}

/* Center cards on tablets and wide screens */
@media (min-width: 769px) {
  .all-members-cards .cards-container {
    justify-content: center;
  }
}

/* Specific fix for medium tablets like 1280x853 */
@media (min-width: 769px) and (max-width: 1024px) {
  .all-members-cards {
    margin: 0 !important;
    width: 100vw !important;
    overflow-x: visible !important;
    padding: 0 2vw !important;
  }
  
  .all-members-cards .cards-container {
    justify-content: center !important;
    overflow-x: visible !important;
    width: 100% !important;
    max-width: none !important;
    display: flex !important;
    align-items: center !important;
    gap: 0 !important;
  }
  
  .all-members-cards .card {
    width: 260px !important;
    height: 360px !important;
    flex: 0 0 260px !important;
  }
  
  .all-members-cards .card-1 {
    margin-left: 0 !important;
  }
  
  .all-members-cards .card-2,
  .all-members-cards .card-3,
  .all-members-cards .card-4,
  .all-members-cards .card-5 {
    margin-left: -40px !important;
  }
  
  .all-members-cards .icon-prev,
  .all-members-cards .icon-next {
    flex-shrink: 0 !important;
    margin-left: 4px !important;
    margin-right: 4px !important;
    display: flex !important;
    z-index: 10 !important;
  }
}

/* Ensure proper centering for larger tablets */
@media (min-width: 1025px) and (max-width: 1440px) {
  .all-members-cards .cards-container {
    justify-content: center;
    max-width: 100%;
  }
}
.all-members-cards .card {
  align-items: center; /* Center items horizontally */
  justify-content: center; /* Center items vertically */
  text-align: center; /* Center all text inside the card */
  background-color: #e6e3b5;
  border-radius: 48px;
  box-shadow: 4px 4px 4px #00000040;
  display: flex;
  flex-direction: column;
  gap: 12px; /* Increased gap for better spacing */
  height: 400px;
  padding: 24px 36px; /* Added vertical padding */
  width: 300px;
  cursor: pointer; /* Added cursor pointer */
}

.all-members-cards .imgh160 {
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 160px;
  justify-content: center;
  mix-blend-mode: multiply;
  position: relative;
  width: 100%;
}

.all-members-cards .image {
  /* height: 116px; */
  /* margin-left: -22.00px; */
  /* margin-right: -22.00px; */
  object-fit: cover;
  /* position: relative; */
  width: -webkit-fill-available;
}

.all-members-cards .card-title-center {
  align-self: stretch;
  color: #000000;
  font-family: "Mona-Sans-SemiBold", Helvetica;
  font-size: 24px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 24px;
  /* position: relative; */
  text-align: center;
}

.all-members-cards .p {
  align-self: stretch;
  color: #000000;
  font-family: "Mona-Sans-Light", Helvetica;
  font-size: 15px; /* Slightly reduced for better fit */
  font-weight: 300;
  letter-spacing: 0;
  line-height: 20px; /* Adjusted line height */
  flex-grow: 1; /* Allow description to take available space */
  overflow: hidden; /* Hide overflow */
}

.all-members-cards .contact-icon-wrapper {
  align-items: center;
  align-self: stretch;
  display: flex;
  flex: 0 0 auto;
  gap: 32px;
  justify-content: center;
  position: relative;
  width: 100%;
}

.all-members-cards .card {
  transition: all 0.3s ease;
  position: relative;
}

.all-members-cards .card:hover {
  transform: translateY(-10px) scale(1.05);
  z-index: 10 !important; /* Higher than any other card */
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

/* Optional: slightly dim other cards when one is hovered */
.all-members-cards:hover .card:not(:hover) {
  filter: brightness(0.9);
}

.all-members-cards .contact-icon {
  height: 36px;
  position: relative;
  width: 36px;
}

.all-members-cards .clickable-icon {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 8px;
  padding: 4px;
}

.all-members-cards .clickable-icon:hover {
  background-color: rgba(59, 114, 62, 0.1);
  transform: scale(1.1);
  box-shadow: 0 2px 8px rgba(59, 114, 62, 0.2);
}
.all-members-cards .card-1 {
  z-index: 5;
}
.all-members-cards .card-2 {
  z-index: 4;
}
.all-members-cards .card-3 {
  z-index: 3;
}
.all-members-cards .card-4 {
  z-index: 2;
}
.all-members-cards .card-5 {
  z-index: 1;
}
.all-members-cards .card-2 ,
.all-members-cards .card-3 ,
.all-members-cards .card-4 ,
.all-members-cards .card-5 {
  margin-left: -64px;
}

/* Override for desktop only */
@media (min-width: 1025px) {
  .all-members-cards .card-2,
  .all-members-cards .card-3,
  .all-members-cards .card-4,
  .all-members-cards .card-5 {
    margin-left: -64px;
  }
}



.all-members-cards .icon-next {
  align-items: center;
  background-color: #ffffff1a;
  border-radius: 100px;
  box-shadow: 0px 4px 4px #00000040;
  display: flex;
  gap: 10px;
  height: 36px;
  justify-content: center;
  margin-left: -2px;
  padding: 10px;
  width: 48px;
}

.all-members-cards .ep-arrow-right-bold-2 {
  height: 24px;
  margin-bottom: -4.00px;
  margin-top: -4.00px;
  position: relative;
  width: 24px;
}


.all-members-cards .image-wrapper {
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 160px;
  justify-content: center;
  position: relative;
  width: 100%;
}

.all-members-cards .image-3 {
  align-self: stretch;
  height: 100px;
  object-fit: cover;
  position: relative;
  width: 100%;
}

.all-members-cards .frame-4 {
  align-items: flex-start;
  align-self: stretch;
  display: flex;
  gap: 10px;
  height: 160px;
  justify-content: center;
  position: relative;
  width: 100%;
}

.all-members-cards .image-4 {
  height: 153px;
  position: relative;
  width: 146px;
}

.all-members-cards .frame-5 {
  align-items: center; /* Center logo container */
  align-self: stretch;
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100px; /* Set a fixed height */
  justify-content: center;
  position: relative;
  width: 100%;
}

.all-members-cards .logo-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.all-members-cards .image-5 {
  max-width: 140px;
  max-height: 90px;
  width: auto;
  height: auto;
  object-fit: contain;
  position: relative;
}

.all-members-cards .biolandes-madagascar {
  align-self: stretch;
  color: #000000;
  font-family: "Mona-Sans-Bold", Helvetica;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 24px;
  position: relative;
  height: 56px; /* Give it a bit more height */
  display: flex;
  align-items: center;
  justify-content: center;
  word-break: break-word; /* Handle long words */
  /* overflow: hidden; */ /* Removed to ensure text is always visible */
}

.members-card-text-logo-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 90px; /* Match max-height of image */
  width: 140px; /* Match max-width of image */
  color: #333333; /* Dark text color */
  font-size: 14px; /* Adjust as needed */
  font-weight: bold;
  text-align: center;
  padding: 8px;
  box-sizing: border-box;
  overflow: hidden;
  line-height: 1.2;
  word-break: break-word;
  background-color: #f0efe2; /* Light background for better visibility */
  border: 1px solid #e0dcc3;
  border-radius: 8px; /* Optional: slight rounding of corners */
}

@media (max-width: 768px){
  .all-members-cards {
    width: 100vw;
    overflow-x: scroll;
    margin: 0;
    padding: 0 4vw;
  }
  .all-members-cards .overlap {
    margin: 4vw;
    width: auto;
  }
  
  .all-members-cards .cards-container {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding-bottom: 16px;
    gap: 16px;
  }
  
  .all-members-cards .cards-container::-webkit-scrollbar {
    display: none;
  }
  
  .all-members-cards .card {
    flex: 0 0 280px;
    width: 280px;
    height: 380px;
    margin-left: 0 !important;
  }
  
  .all-members-cards .icon-prev,
  .all-members-cards .icon-next {
    display: none;
  }
  
  /* Mobile styles for no results message */
  .all-members-cards .no-results-message {
    width: 90vw;
    padding: 24px;
    font-size: 14px;
  }
  
  .all-members-cards .no-results-message h3 {
    font-size: 20px;
  }
  
  .all-members-cards .no-results-message p {
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .all-members-cards {
    padding: 0 2vw;
  }
  
  .all-members-cards .overlap {
    margin: 2vw;
  }
  
  .all-members-cards .card {
    flex: 0 0 260px;
    width: 260px;
    height: 360px;
    padding: 20px 24px;
    gap: 10px;
  }
  
  .all-members-cards .biolandes-madagascar {
    font-size: 18px;
    line-height: 22px;
    height: 48px;
  }
  
  .all-members-cards .p {
    font-size: 14px;
    line-height: 18px;
  }
  
  .all-members-cards .image-5 {
    max-width: 120px;
    max-height: 80px;
  }
  
  .members-card-text-logo-fallback {
    width: 120px;
    height: 80px;
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .all-members-cards {
    padding: 0 1vw;
  }
  
  .all-members-cards .overlap {
    margin: 1vw;
  }
  
  .all-members-cards .card {
    flex: 0 0 240px;
    width: 240px;
    height: 340px;
    padding: 16px 20px;
    gap: 8px;
    border-radius: 32px;
  }
  
  .all-members-cards .biolandes-madagascar {
    font-size: 16px;
    line-height: 20px;
    height: 44px;
  }
  
  .all-members-cards .p {
    font-size: 13px;
    line-height: 16px;
  }
  
  .all-members-cards .image-5 {
    max-width: 100px;
    max-height: 70px;
  }
  
  .members-card-text-logo-fallback {
    width: 100px;
    height: 70px;
    font-size: 11px;
  }
  
  .all-members-cards .imgh160 {
    height: 140px;
  }
  
  .all-members-cards .contact-icon {
    height: 28px;
    width: 28px;
  }
  
  .all-members-cards .no-results-message {
    width: 95vw;
    padding: 16px;
    border-radius: 24px;
  }
  
  .all-members-cards .no-results-message h3 {
    font-size: 18px;
  }
  
  .all-members-cards .no-results-message p {
    font-size: 13px;
  }
}

/* Pagination container styling */
.pagination-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 12px;
  margin-bottom: 8px;
}

/* Media queries for pagination container */
@media (min-width: 769px) {
  .pagination-container {
    margin-top: 16px;
    margin-bottom: 12px;
  }
}

@media (min-width: 1025px) {
  .pagination-container {
    margin-top: 20px;
    margin-bottom: 16px;
  }
}
</style>